import { lazy, Suspense, useState, useEffect } from "react";
import { esLeccion } from "@/lib/pantallaDeLeccion.mjs";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, LogOut, User, Sparkles } from "lucide-react";
import AALogo from "./AALogo";
import StreakWidget from "./StreakWidget";
import { NAV_ITEMS } from "@/data/navegacion";
import BurbujasDelMenu from "./BurbujasDelMenu";
import { enlacesPara } from "@/data/cuenta";
import { useAuth } from "@/context/AuthContext";
import { useAvatar } from "@/hooks/useAvatar";
import { startCheckout } from "@/lib/api";


// EL MODAL DE «HAZTE INDIVIDUAL» VIAJA CUANDO SE ABRE, NO ANTES. Lo traía la barra a pelo, y
// con él la lista entera de técnicas del Lab (`data/alphaLab.js`): unos 4 kB de la primera carga
// para algo que solo ve quien está en gratis y además lo pulsa. Se monta la primera vez que se
// abre y se queda montado, para que la animación de cerrar siga saliendo.
const PremiumUpgradeModal = lazy(() => import("./PremiumUpgradeModal"));
// EL AVATAR LLEGA UN MOMENTO DESPUES, y quien no ha entrado no lo descarga nunca. Son 36 kB
// de fuente -el cuerpo y la estrella, que dibujan el muñeco entero- viajando en la PRIMERA
// carga para pintar treinta pixeles en la barra, y solo a quien tiene sesion: el visitante
// que abre la portada los pagaba sin verlos. Mientras viaja se enseña el mismo icono de
// usuario que ve quien todavia no se ha hecho uno, asi que no hay hueco ni salto.
const AlphaAvatar = lazy(() => import("./AlphaAvatar"));

// AQUI SE PIDIO EL CATALOGO DURANTE UNA TARDE, el 17 de octubre de 2026: cada burbuja de
// competencia decia los dos o tres cursos publicados que tenia detras, en vez de sus materias.
// Gabri lo vio puesto y lo retiro en el momento: *«no pongas ningun curso debajo de la
// competencia de la navbar. se ve muy grueso»*. Y es cierto en cuanto se mira: «Historia de la
// Filosofia (PAU Catalunya) +1» son dos lineas de titulos largos en un recuadro de menu, y
// seis recuadros asi dejan de ser un menu para ser una pagina. **Un menu dice a donde se va, no
// lo que hay al llegar.** Queda escrito para que no se vuelva a intentar sin saber que se
// intento: de paso, la barra dejo de pedirle el catalogo al servidor en cada visita.

export default function Navbar() {
  const [openBubble, setOpenBubble] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { features: avatarFeatures } = useAvatar();
  const [userMenu, setUserMenu] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [premiumMontado, setPremiumMontado] = useState(false);
  if (premiumOpen && !premiumMontado) setPremiumMontado(true);

  // SOLO SE LE ENSENA A QUIEN ESTA EN GRATIS. Antes lo veia todo el mundo: un anonimo -que
  // ya tiene «Precios» al lado-, el administrador y, sobre todo, quien YA PAGA. Pedirle que
  // se suscriba a alguien suscrito es la forma mas rapida de parecer un sitio que no sabe
  // quien eres. El plan lo dice el servidor en la respuesta de la cuenta; aqui solo se lee.
  const enGratis = Boolean(user) && user.plan === "free";

  // Y LLEVA A PAGAR, no a la pagina de precios. `startCheckout` incrusta el id del alumno en
  // el enlace, que es lo que permite al webhook saber a quien abrirle el plan. Si la pasarela
  // no contesta, se cae a `/precios`, que es la via manual de siempre.
  const irAlPago = async () => {
    setPremiumOpen(false);
    try {
      const { url } = await startCheckout("individual", "monthly");
      window.location.href = url;
    } catch {
      navigate("/precios");
    }
  };

  const sal = async () => { setMobileOpen(false); await logout(); navigate("/"); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenBubble(null);
  }, [location.pathname]);

  // CON EL MENÚ DEL MÓVIL ABIERTO, EL BOTÓN DEL MENTOR SE VA: estaba encima de «Salir». Se dice
  // en el `<html>` y lo esconde `index.css`, porque el botón vive en otro trozo y no sabe del menú.
  useEffect(() => {
    try { document.documentElement.dataset.menuMovil = mobileOpen ? "abierto" : ""; } catch { /* sin DOM */ }
  }, [mobileOpen]);

  return (
    <div className={esLeccion(location.pathname) ? "max-md:hidden" : "contents"}>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] transition-all duration-300 ${
          scrolled ? "bubble-glass shadow-[0_1px_0_rgba(0,0,0,0.06)]" : "bg-white/80 backdrop-blur"
        }`}
        data-testid="site-navbar"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-home-link">
            <AALogo size={32} lockup="horizontal" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" onMouseLeave={() => setOpenBubble(null)}>
            {NAV_ITEMS.map((item) => {
              // UN GRUPO SIN PÁGINA SE ABRE, NO NAVEGA. «Herramientas» no es un sitio: es un
              // cajón con cinco. Pulsarlo llevaba a `undefined` —o sea, a ninguna parte y sin
              // avisar—, así que lo que hace es lo mismo que pasar el ratón por encima: abrir.
              // Eso además lo deja usable con el teclado, que con `onMouseEnter` a secas no lo
              // estaba.
              const activo = item.sinPagina
                ? (item.bubbles || []).some(
                  (b) => location.pathname.startsWith(String(b.to || "").split("#")[0]))
                : !!item.to && item.to !== "/" && location.pathname.startsWith(item.to);
              return (
                <div
                  key={item.key}
                  className="relative"
                  onMouseEnter={() => item.bubbles && setOpenBubble(item.key)}
                >
                  <button
                    onClick={() => (item.sinPagina ? setOpenBubble(item.key) : navigate(item.to))}
                    onFocus={() => item.bubbles && setOpenBubble(item.key)}
                    aria-expanded={item.bubbles ? openBubble === item.key : undefined}
                    data-testid={`nav-${item.key}-link`}
                    className={`px-4 py-2 text-[13px] font-medium tracking-wide uppercase font-mono-alpha transition-colors ${
                      activo ? "text-alpha-red" : "text-alpha-black hover:text-alpha-red"
                    }`}
                  >
                    {item.label}
                  </button>
                </div>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user?.role === "admin" && (
              <Link
                to="/admin/cms"
                data-testid="nav-cms"
                className="px-4 py-2 text-[13px] font-mono-alpha uppercase tracking-wide text-alpha-red hover:text-alpha-black transition-colors"
              >
                CMS
              </Link>
            )}
            {enGratis && (
              <button
                onClick={() => setPremiumOpen(true)}
                data-testid="nav-premium"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-mono-alpha uppercase tracking-wide text-alpha-black hover:text-alpha-red transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5 text-alpha-red" /> Hazte Individual
              </button>
            )}
            {user ? (
              <>
                <StreakWidget />
                <div
                  className="relative"
                  onMouseEnter={() => setUserMenu(true)}
                  onMouseLeave={() => setUserMenu(false)}
                >
                  <button
                    data-testid="nav-user-link"
                    onClick={() => navigate("/alpha")}
                    className="px-3 py-1.5 text-[13px] font-mono-alpha uppercase tracking-wide text-alpha-black hover:text-alpha-red transition-colors inline-flex items-center gap-2"
                  >
                    {avatarFeatures ? (
                      <Suspense fallback={<User className="w-3.5 h-3.5" />}>
                        <AlphaAvatar features={avatarFeatures} size={30} />
                      </Suspense>
                    ) : (
                      <User className="w-3.5 h-3.5" />
                    )}
                    {user.name?.split(" ")[0] || "Mi cuenta"}
                  </button>
                  <AnimatePresence>
                    {userMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full pt-2 w-56 z-50"
                        data-testid="nav-user-menu"
                      >
                        <div className="rounded-caja bg-white border border-alpha-black/15 shadow-xl">
                          {/* LA LISTA ES LA MISMA QUE LA DEL MÓVIL: `data/cuenta.js`. */}
                          {enlacesPara(user, !!avatarFeatures).map((e) => (
                            <Link
                              key={e.to}
                              to={e.to}
                              data-testid={e.testid}
                              className={`block px-4 py-3 text-[11px] font-mono-alpha uppercase tracking-widest hover:bg-alpha-off border-b border-alpha-black/5 ${e.rojo ? "text-alpha-red" : ""}`}
                            >
                              {e.label}
                            </Link>
                          ))}
                          <div className="px-4 py-2.5 text-[10px] font-mono-alpha text-alpha-black/40 truncate">
                            {user.email}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <button
                  onClick={sal}
                  data-testid="nav-logout"
                  className="rounded-full px-4 py-2 text-[13px] font-mono-alpha uppercase tracking-wide border border-alpha-black hover:bg-alpha-black hover:text-white transition-colors inline-flex items-center gap-2"
                >
                  <LogOut className="w-3.5 h-3.5" /> Salir
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="rounded-full px-4 py-2 text-[13px] font-mono-alpha uppercase tracking-wide border border-alpha-black hover:bg-alpha-black hover:text-white transition-colors"
              >
                Entrar
              </Link>
            )}
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Floating Bubbles Panel */}
        <AnimatePresence>
          {openBubble && NAV_ITEMS.find((i) => i.key === openBubble)?.bubbles && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 top-full pt-3 hidden lg:block"
              onMouseEnter={() => setOpenBubble(openBubble)}
              onMouseLeave={() => setOpenBubble(null)}
            >
              <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="bubble-glass p-6 rounded-2xl border border-black/10 shadow-xl">
                  <BurbujasDelMenu
                    menu={openBubble}
                    burbujas={NAV_ITEMS.find((i) => i.key === openBubble)?.bubbles}
                    pie={NAV_ITEMS.find((i) => i.key === openBubble)?.pie}
                    onIr={(b) => navigate(
                      b.to || NAV_ITEMS.find((it) => it.key === openBubble).to,
                    )}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-white lg:hidden pt-16 overflow-y-auto overscroll-contain"
            data-testid="nav-mobile-panel"
          >
            <div className="p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] flex flex-col gap-1">
              {NAV_ITEMS.map((item, i) => (
                <motion.div
                  key={item.key}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                >
                  {/* EN EL PANEL ESTRECHO, UN GRUPO SE ABRE ENTERO. Aquí no hay sitio ni gesto
                      para un segundo nivel: lo que cabe es la lista. El grupo pone su nombre de
                      rótulo y sus cinco debajo, más pequeñas — que es lo que el desplegable de
                      arriba hace al pasar el ratón, dicho de la única manera que funciona sin
                      ratón. */}
                  {item.sinPagina ? (
                    <div className="py-4 border-b border-black/10" data-testid={`nav-mobile-${item.key}`}>
                      <div className="font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-black/40">
                        {item.label}
                      </div>
                      <div className="mt-2 flex flex-col">
                        {(item.bubbles || []).map((b) => (
                          <Link
                            key={b.to}
                            to={b.to}
                            className="py-2 font-serif-display text-2xl"
                          >
                            {b.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.to}
                      className="block py-4 border-b border-black/10 font-serif-display text-3xl"
                      data-testid={`nav-mobile-${item.key}`}
                    >
                      {item.label}
                    </Link>
                  )}
                </motion.div>
              ))}
              {enGratis && (
                <button
                  onClick={() => { setMobileOpen(false); setPremiumOpen(true); }}
                  className="rounded-full mt-4 px-4 py-3 flex items-center justify-center gap-2 border border-alpha-black font-mono-alpha uppercase tracking-wide"
                  data-testid="nav-mobile-premium"
                >
                  <Sparkles className="w-4 h-4 text-alpha-red" /> Hazte Individual
                </button>
              )}
              {/* TU CUENTA, EN EL MÓVIL TAMBIÉN (roadmap móvil, 1.2). Hasta el 10 de octubre
                  aquí no había ni Entrar ni Salir: en el móvil no se podía cerrar la sesión. */}
              <div className="mt-6 pt-4 border-t border-black/10" data-testid="nav-mobile-cuenta">
                <div className="font-mono-alpha text-[11px] uppercase tracking-widest text-alpha-black/50 mb-2">
                  {user ? (user.name?.split(" ")[0] || "Tu cuenta") : "Tu cuenta"}
                </div>
                {user ? (
                  <>
                    {enlacesPara(user, !!avatarFeatures).map((e) => (
                      <Link key={e.to} to={e.to} data-testid={`nav-mobile-${e.testid.slice(4)}`}
                        className={`flex items-center min-h-[44px] text-[17px] ${e.rojo ? "text-alpha-red" : ""}`}>
                        {e.label}
                      </Link>
                    ))}
                    <button onClick={sal} data-testid="nav-mobile-logout"
                      className="rounded-full mt-4 w-full min-h-[48px] inline-flex items-center justify-center gap-2 border border-alpha-black font-mono-alpha uppercase tracking-wide">
                      <LogOut className="w-4 h-4" /> Salir
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-3">
                    <Link to="/login" data-testid="nav-mobile-login"
                      className="rounded-caja min-h-[48px] inline-flex items-center justify-center border border-alpha-black font-mono-alpha uppercase tracking-wide">
                      Entrar
                    </Link>
                    <Link to="/register" data-testid="nav-mobile-register"
                      className="min-h-[48px] inline-flex items-center justify-center bg-alpha-black text-white font-mono-alpha uppercase tracking-wide">
                      Crear cuenta
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {premiumMontado && (
        <Suspense fallback={null}>
          <PremiumUpgradeModal
            open={premiumOpen}
            onClose={() => setPremiumOpen(false)}
            onUpgrade={irAlPago}
          />
        </Suspense>
      )}

      <div className="h-16" />
    </div>
  );
}
